<script setup lang="ts">
/**
 * Fiche de remise de matériel signable (modale) — module Onboarding V2.
 *
 * Numérisation de la « Fiche de dotation d'équipements informatiques »
 * (P10-SI-GP-FORM1-V1). Reprend l'identité du collaborateur, l'inventaire éditable
 * des équipements (hors attributs sensibles 🔒), un lieu (« Fait à ») et deux
 * signatures tactiles. À la validation, construit un `HandoverDocument` et l'envoie
 * au store.
 *
 * Pattern modale natif `<dialog class="modal">` DaisyUI — cf. ExportsCegidAdminPage.vue.
 * Le parent récupère un `ref` et appelle `open()` (exposé via defineExpose).
 */
import { computed, ref } from 'vue'
import { PrinterIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { useOnboardingStore } from '@/v2/stores/useOnboardingStore'
import { CONDITION_LABEL, isSensitiveAttribute } from '@/v2/onboarding/catalog'
import type {
  AssetAssignment,
  HandoverDocument,
  HandoverLineItem,
  OnboardingCase,
} from '@/v2/onboarding/types'
import OnbSignaturePad from './OnbSignaturePad.vue'

const props = defineProps<{
  caseData: OnboardingCase
}>()

const emit = defineEmits<{
  saved: [doc: HandoverDocument]
}>()

const store = useOnboardingStore()

const dialog = ref<HTMLDialogElement | null>(null)

/** Date du jour, figée à l'ouverture de la fiche (affichage + horodatage). */
const today = new Date().toLocaleDateString('fr-FR')

/** « Fait à … » */
const place = ref('')
const collaboratorSig = ref<string | null>(null)
const responsibleSig = ref<string | null>(null)

/** Lignes d'inventaire éditables (état, présence, commentaire). */
const lineItems = ref<HandoverLineItem[]>([])

/** Construit le « N° Série / IMEI » affichable depuis les attributs de l'objet. */
function serialOrImei(asset: AssetAssignment): string {
  const parts = [asset.attributes.serialNumber, asset.attributes.imei].filter(
    (v): v is string => !!v,
  )
  return parts.join(' / ')
}

/**
 * (Ré)initialise les lignes depuis les assets du dossier. Exclut les objets
 * REPLACED (anciens/inactifs). N'expose aucun attribut sensible (🔒) : seuls
 * `serialNumber` et `imei` sont repris, et ni l'un ni l'autre n'est sensible
 * dans le catalogue — on filtre néanmoins par sécurité.
 */
function buildLineItems(): HandoverLineItem[] {
  const isOffboarding = props.caseData.type === 'OFFBOARDING'
  return props.caseData.assets
    .filter((a) => a.status !== 'REPLACED')
    .map((a) => {
      const serialOk = a.attributes.serialNumber && !isSensitiveAttribute('serialNumber')
      const imeiOk = a.attributes.imei && !isSensitiveAttribute('imei')
      const idParts = [serialOk ? a.attributes.serialNumber : '', imeiOk ? a.attributes.imei : '']
        .filter(Boolean)
        .join(' / ')
      // Pré-remplissage de l'état réel : restitution → état de retour ;
      // remise (onboarding) → état à la fourniture, « Neuf » par défaut.
      const state = isOffboarding
        ? (a.returnCondition ? CONDITION_LABEL[a.returnCondition] ?? '' : '')
        : a.condition
          ? CONDITION_LABEL[a.condition] ?? 'Neuf'
          : 'Neuf'
      return {
        label: a.label,
        serialOrImei: idParts || serialOrImei(a),
        state,
        comment: '',
        present: true,
      }
    })
}

/** Les deux signatures sont requises pour valider la remise. */
const canValidate = computed(() => !!collaboratorSig.value && !!responsibleSig.value)

/** Ouvre la modale (API publique consommée par le parent). */
function open(): void {
  // Réinitialise à chaque ouverture pour repartir d'une fiche vierge.
  lineItems.value = buildLineItems()
  place.value = ''
  collaboratorSig.value = null
  responsibleSig.value = null
  dialog.value?.showModal()
}

function close(): void {
  dialog.value?.close()
}

defineExpose({ open })

function validate(): void {
  if (!canValidate.value) return
  const doc: HandoverDocument = {
    id: crypto.randomUUID(),
    caseId: props.caseData.id,
    kind: 'HANDOVER',
    formReference: 'P10-SI-GP-FORM1-V1',
    createdAt: new Date().toISOString(),
    place: place.value,
    collaboratorSignature: collaboratorSig.value,
    responsibleSignature: responsibleSig.value,
    signedAt: new Date().toISOString(),
    lineItems: lineItems.value,
  }
  store.saveHandover(props.caseData.id, doc)
  emit('saved', doc)
  close()
}

function print(): void {
  window.print()
}

const titleId = 'onb-handover-title'

/** Nom complet du collaborateur pour l'en-tête. */
const fullName = computed(
  () => `${props.caseData.collaborator.lastName} ${props.caseData.collaborator.firstName}`,
)

/** YYYY-MM-DD → DD/MM/YYYY (sans Date : pas de surprise de fuseau). */
function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}
</script>

<template>
  <dialog ref="dialog" class="modal" :aria-labelledby="titleId">
    <div class="modal-box w-full max-w-4xl max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
      <!-- En-tête + fermeture -->
      <div class="flex items-start justify-between gap-4">
        <div>
          <h2 :id="titleId" class="text-lg font-semibold">Fiche de remise de matériel</h2>
          <p class="text-sm text-base-content/70">Formulaire P10-SI-GP-FORM1-V1</p>
        </div>
        <button
          type="button"
          class="btn btn-ghost btn-sm motion-reduce:transition-none"
          aria-label="Fermer"
          @click="close"
        >
          <XMarkIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <!-- Identité du collaborateur -->
      <section class="mt-4" aria-labelledby="onb-handover-identity">
        <h3 id="onb-handover-identity" class="sr-only">Identité du collaborateur</h3>
        <dl class="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          <div>
            <dt class="text-sm text-base-content/70">NOM</dt>
            <dd class="font-medium text-base-content">{{ caseData.collaborator.lastName }}</dd>
          </div>
          <div>
            <dt class="text-sm text-base-content/70">Prénom</dt>
            <dd class="font-medium text-base-content">{{ caseData.collaborator.firstName }}</dd>
          </div>
          <div>
            <dt class="text-sm text-base-content/70">Entité</dt>
            <dd class="text-base-content">{{ caseData.collaborator.entity }}</dd>
          </div>
          <div>
            <dt class="text-sm text-base-content/70">N+1 (manager)</dt>
            <dd class="text-base-content">{{ caseData.collaborator.manager }}</dd>
          </div>
          <div>
            <dt class="text-sm text-base-content/70">Date d'arrivée</dt>
            <dd class="tabular-nums text-base-content">{{ formatDay(caseData.arrivalDate) }}</dd>
          </div>
          <div>
            <dt class="text-sm text-base-content/70">Date de sortie</dt>
            <dd class="tabular-nums text-base-content">{{ formatDay(caseData.exitDate) }}</dd>
          </div>
        </dl>
      </section>

      <!-- Tableau des équipements -->
      <section class="mt-6" aria-labelledby="onb-handover-equipment">
        <h3 id="onb-handover-equipment" class="text-base font-semibold">Équipements remis</h3>
        <div class="mt-2 overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th scope="col">Équipement</th>
                <th scope="col">N° Série / IMEI</th>
                <th scope="col">État</th>
                <th scope="col" class="text-center">Présent</th>
                <th scope="col">Commentaire</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!lineItems.length">
                <td colspan="5" class="text-center text-base-content/80">
                  Aucun équipement à remettre sur ce dossier.
                </td>
              </tr>
              <tr v-for="(item, index) in lineItems" :key="index">
                <td class="font-medium text-base-content">{{ item.label }}</td>
                <td class="tabular-nums text-base-content/80">{{ item.serialOrImei || '—' }}</td>
                <td>
                  <input
                    v-model="item.state"
                    type="text"
                    class="input input-bordered input-sm w-28"
                    :aria-label="`État de ${item.label}`"
                  />
                </td>
                <td class="text-center">
                  <input
                    v-model="item.present"
                    type="checkbox"
                    class="checkbox"
                    :aria-label="`${item.label} présent`"
                  />
                </td>
                <td>
                  <input
                    v-model="item.comment"
                    type="text"
                    class="input input-bordered input-sm w-full min-w-40"
                    :aria-label="`Commentaire pour ${item.label}`"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Lieu + date -->
      <section class="mt-6 flex flex-wrap items-end gap-4" aria-labelledby="onb-handover-place">
        <h3 id="onb-handover-place" class="sr-only">Lieu et date</h3>
        <div>
          <label class="label" for="onb-handover-place-input">
            <span class="label-text">Fait à</span>
          </label>
          <input
            id="onb-handover-place-input"
            v-model="place"
            type="text"
            class="input input-bordered"
            placeholder="Ville"
          />
        </div>
        <div>
          <span class="label-text block">Le</span>
          <span class="mt-1 block tabular-nums text-base-content">{{ today }}</span>
        </div>
      </section>

      <!-- Signatures -->
      <section class="mt-6" aria-labelledby="onb-handover-signatures">
        <h3 id="onb-handover-signatures" class="text-base font-semibold">Signatures</h3>
        <div class="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <OnbSignaturePad v-model="collaboratorSig" label="Pour le collaborateur" />
          <OnbSignaturePad v-model="responsibleSig" label="Pour l'entreprise" />
        </div>
        <p v-if="!canValidate" class="mt-2 text-sm text-base-content/80">
          Les deux signatures sont requises pour valider la remise.
        </p>
      </section>

      <!-- Actions -->
      <div class="modal-action flex-wrap">
        <button type="button" class="btn btn-ghost motion-reduce:transition-none" @click="print">
          <PrinterIcon class="h-5 w-5" aria-hidden="true" />
          Imprimer
        </button>
        <button type="button" class="btn btn-ghost motion-reduce:transition-none" @click="close">
          Annuler
        </button>
        <button
          type="button"
          class="btn btn-primary motion-reduce:transition-none"
          :disabled="!canValidate"
          @click="validate"
        >
          Valider la remise
        </button>
      </div>
    </div>

    <form method="dialog" class="modal-backdrop">
      <button aria-label="Fermer">Fermer</button>
    </form>
  </dialog>
</template>
